import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Form, Grid, Icon, Message, Modal } from 'semantic-ui-react';
import { usePopup, closePopup } from '../../lib/popup';
import { Markdown } from '../../lib/custom-ui';

import { startStopwatch, stopStopwatch } from '../../utils/stopwatch';
import { calculateCoefficient } from '../../utils/calculateCoefficient';
import { formatPoints } from '../../utils/formatPoints';
import NameField from './NameField';
import DescriptionEdit from './DescriptionEdit';
import Tasks from './Tasks';
import Attachments from './Attachments';
import AttachmentAddZone from './AttachmentAddZone';
import AttachmentAddStep from './AttachmentAddStep';
import Activities from './Activities';
import User from '../User';
import Label from '../Label';
import DueDate from '../DueDate';
import Stopwatch from '../Stopwatch';
import BoardMembershipsStep from '../BoardMembershipsStep';
import LabelsStep from '../LabelsStep';
import DueDateEditStep from '../DueDateEditStep';
import StopwatchEditStep from '../StopwatchEditStep';
import CardMoveStep from '../CardMoveStep';
import DeleteStep from '../DeleteStep';
import TaskCloseDateStep from '../TaskCloseDateStep';
import CardReminderStep from '../CardReminderStep/CardReminderStep';

import styles from './CardModal.module.scss';

const CardModal = React.memo(
  ({
    name,
    description,
    dueDate,
    reminderDate,
    reminderEnabled,
    author,
    stopwatch,
    createdAt,
    isSubscribed,
    isPointSystemEnabled,
    isActivitiesFetching,
    isAllActivitiesFetched,
    isActivitiesDetailsVisible,
    isActivitiesDetailsFetching,
    listId,
    boardId,
    projectId,
    cardId,
    pointSystemId,
    users,
    labels,
    tasks,
    attachments,
    activities,
    allProjectsToLists,
    allBoardMemberships,
    allLabels,
    contentCardDefaults,
    cardPointData,
    cardPointAdvancedData,
    cardPointArchivedData,
    contentViewPlan,
    dampingCoefficients,
    canEdit,
    canEditCommentActivities,
    canEditAllCommentActivities,
    currentUser,
    isSuperuser,
    currentListReportData,
    transferLists,
    onContentCardDefaultsFetch,
    onContentViewPlanGetOrCreate,
    onDampingCoefficientsFetch,
    onCardPointDataFetch,
    onCardPointDataUpsert,
    onCardPointDataComplete,
    onCardPointDataUncomplete,
    onCardPointAdvancedDataFetch,
    onCardPointAdvancedDataCreate,
    onCardPointAdvancedDataUpdate,
    onCardPointAdvancedDataDelete,
    onUpdate,
    onMove,
    onTransfer,
    onDelete,
    onArchive,
    onUserAdd,
    onUserRemove,
    onBoardFetch,
    onLabelAdd,
    onLabelRemove,
    onLabelCreate,
    onLabelUpdate,
    onLabelMove,
    onLabelDelete,
    onTaskCreate,
    onTaskUpdate,
    onTaskMove,
    onTaskDelete,
    onAttachmentCreate,
    onAttachmentUpdate,
    onAttachmentDelete,
    onActivitiesFetch,
    onActivitiesDetailsToggle,
    onCommentActivityCreate,
    onCommentActivityUpdate,
    onCommentActivityDelete,
    onClose,
  }) => {
    const [t] = useTranslation();

    const isGalleryOpened = useRef(false);
    const isPointSystemInitialized = useRef(false);
    const shouldShowNoCoefficientMessageRef = useRef(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCcdId, setSelectedCcdId] = useState('');
    const [advancedDraftsById, setAdvancedDraftsById] = useState({});
    const wasTaskClosedRef = useRef(cardPointData?.closed === true);

    const updated_at = new Date(createdAt);
    updated_at.setHours(updated_at.getHours() + 3);
    const updatedAtFormated = updated_at.toISOString().slice(0, 16).replace('T', ' ');

    const closedAtFormated = useMemo(() => {
      const raw =
        cardPointArchivedData?.taskClosedAt || (cardPointData?.closed === true ? cardPointData?.closedAt : null);

      if (!raw) {
        return null;
      }

      try {
        const closedAt = new Date(raw);
        if (isNaN(closedAt.getTime())) {
          return null;
        }
        closedAt.setHours(closedAt.getHours() + 3);
        return closedAt.toISOString().slice(0, 16).replace('T', ' ');
      } catch (error) {
        return null;
      }
    }, [cardPointArchivedData?.taskClosedAt, cardPointData?.closed, cardPointData?.closedAt]);
    const handleToggleStopwatchClick = useCallback(() => {
      onUpdate({
        stopwatch: stopwatch.startedAt ? stopStopwatch(stopwatch) : startStopwatch(stopwatch),
      });
    }, [stopwatch, onUpdate]);

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    const handleDescriptionUpdate = useCallback(
      (newDescription) => {
        onUpdate({
          description: newDescription,
        });
      },
      [onUpdate],
    );

    const handleDueDateUpdate = useCallback(
      (newDueDate) => {
        onUpdate({
          dueDate: newDueDate,
        });
      },
      [onUpdate],
    );

    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

    const handleReminderClick = useCallback(() => {
      if (canEdit) {
        setIsReminderModalOpen(true);
      }
    }, [canEdit]);

    const handleReminderApply = useCallback(
      ({ date, enabled }) => {
        onUpdate({
          reminderDate: date,
          reminderEnabled: Boolean(enabled),
        });
      },
      [onUpdate],
    );

    const handleReminderClose = useCallback(() => {
      setIsReminderModalOpen(false);
    }, []);

    const reminderButtonText = useMemo(() => {
      if (reminderEnabled && reminderDate) {
        return `Напоминание: ${t('format:dateTime', {
          postProcess: 'formatDate',
          value: reminderDate,
        })}`;
      }
      return 'Напоминание';
    }, [reminderEnabled, reminderDate, t]);

    const handleStopwatchUpdate = useCallback(
      (newStopwatch) => {
        onUpdate({
          stopwatch: newStopwatch,
        });
      },
      [onUpdate],
    );

    const handleCoverUpdate = useCallback(
      (newCoverAttachmentId) => {
        onUpdate({
          coverAttachmentId: newCoverAttachmentId,
        });
      },
      [onUpdate],
    );

    const handleToggleSubscriptionClick = useCallback(() => {
      onUpdate({
        isSubscribed: !isSubscribed,
      });
    }, [isSubscribed, onUpdate]);

    const handleGalleryOpen = useCallback(() => {
      isGalleryOpened.current = true;
    }, []);

    const handleGalleryClose = useCallback(() => {
      isGalleryOpened.current = false;
    }, []);

    // Валидация: проверяем есть ли записи где value != 0 и description пустое
    const hasInvalidAdvancedData = useMemo(() => {
      if (!isPointSystemEnabled || !cardPointAdvancedData) {
        return false;
      }

      return (cardPointAdvancedData || []).some((it) => {
        const draft = advancedDraftsById[String(it.id)];
        const value = draft ? Number(draft.value) || 0 : (it.value ?? 0);
        const description = draft ? (draft.description ?? '') : (it.description ?? '');
        
        return value !== 0 && (!description || description.trim() === '');
      });
    }, [isPointSystemEnabled, cardPointAdvancedData, advancedDraftsById]);

    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [showCloseBlockedMessage, setShowCloseBlockedMessage] = useState(false);

    // Скрываем сообщение, когда ошибки исправлены
    useEffect(() => {
      if (!hasInvalidAdvancedData && showValidationMessage) {
        setShowValidationMessage(false);
      }
    }, [hasInvalidAdvancedData, showValidationMessage]);

    const handleClose = useCallback(() => {
      if (isGalleryOpened.current) {
        return;
      }

      if (shouldShowNoCoefficientMessageRef.current) {
        setShowCloseBlockedMessage(true);
        setTimeout(() => {
          setShowCloseBlockedMessage(false);
        }, 3000);
        return;
      }

      // Проверяем наличие невалидных записей
      if (hasInvalidAdvancedData) {
        setShowValidationMessage(true);
        // Скрываем сообщение через 3 секунды
        setTimeout(() => {
          setShowValidationMessage(false);
        }, 3000);
        return;
      }

      // Удаляем записи где value == 0 и description пустое
      // Проверяем и черновики, и реальные данные
      if (isPointSystemEnabled && cardPointAdvancedData) {
        const recordsToDelete = (cardPointAdvancedData || []).filter((it) => {
          const draft = advancedDraftsById[String(it.id)];
          // Если есть черновик, используем его значения, иначе реальные данные
          const value = draft ? Number(draft.value) || 0 : (it.value ?? 0);
          const description = draft ? (draft.description ?? '') : (it.description ?? '');
          
          return value === 0 && (!description || description.trim() === '');
        });

        recordsToDelete.forEach((record) => {
          onCardPointAdvancedDataDelete(String(record.id));
        });
      }

      onClose();
    }, [
      onClose,
      isPointSystemEnabled,
      cardPointAdvancedData,
      advancedDraftsById,
      onCardPointAdvancedDataDelete,
      hasInvalidAdvancedData,
    ]);

    const AttachmentAddPopup = usePopup(AttachmentAddStep);
    const BoardMembershipsPopup = usePopup(BoardMembershipsStep);
    const LabelsPopup = usePopup(LabelsStep);
    const DueDateEditPopup = usePopup(DueDateEditStep);
    const StopwatchEditPopup = usePopup(StopwatchEditStep);
    const CardMovePopup = usePopup(CardMoveStep);
    const DeletePopup = usePopup(DeleteStep);
    
    const [isTaskCloseDatePopupOpen, setIsTaskCloseDatePopupOpen] = useState(false);
    const [pendingTaskClosedAt, setPendingTaskClosedAt] = useState(null);
    const [isMonthMismatchModalOpen, setIsMonthMismatchModalOpen] = useState(false);
    const [isMoveListModalOpen, setIsMoveListModalOpen] = useState(false);
    const [selectedMoveListId, setSelectedMoveListId] = useState('');

    const userIds = users.map((user) => user.id);
    const labelIds = labels.map((label) => label.id);
    
    // Формируем список подписчиков (пока только текущий пользователь, если он подписан)
    const subscribers = useMemo(() => {
      // Не визуализируем подписчика, если он уже выбран исполнителем (чтобы не дублировать в UI)
      if (isSubscribed && currentUser && !userIds.includes(currentUser.id)) {
        return [currentUser];
      }
      return [];
    }, [isSubscribed, currentUser, userIds]);

    const handleUserSelect = useCallback(
      (userId) => {
        // Если выбранный пользователь уже является исполнителем, удаляем его
        if (userIds.includes(userId)) {
          onUserRemove(userId);
          return;
        }

        // Удаляем всех существующих исполнителей
        userIds.forEach((id) => {
          onUserRemove(id);
        });

        // Добавляем нового исполнителя
        onUserAdd(userId);
      },
      [userIds, onUserAdd, onUserRemove],
    );

    useEffect(() => {
      if (!isPointSystemEnabled || !pointSystemId || !cardId) {
        return;
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      onContentCardDefaultsFetch();
      onContentViewPlanGetOrCreate(year, month, pointSystemId);
      onDampingCoefficientsFetch(pointSystemId);
      onCardPointDataFetch();
      onCardPointAdvancedDataFetch();
    }, [
      isPointSystemEnabled,
      pointSystemId,
      cardId,
      onContentCardDefaultsFetch,
      onContentViewPlanGetOrCreate,
      onDampingCoefficientsFetch,
      onCardPointDataFetch,
      onCardPointAdvancedDataFetch,
    ]);

    useEffect(() => {
      if (!isPointSystemEnabled) {
        return;
      }

      const items = cardPointAdvancedData || [];
      const ids = new Set(items.map((it) => String(it.id)));

      setAdvancedDraftsById((prev) => {
        let changed = false;
        let next = prev;

        items.forEach((it) => {
          const id = String(it.id);
          if (next[id]) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[id] = {
            value: it.value ?? 0,
            description: it.description ?? '',
          };
        });

        Object.keys(next).forEach((id) => {
          if (ids.has(id)) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          delete next[id];
        });

        return changed ? next : prev;
      });
    }, [isPointSystemEnabled, cardPointAdvancedData]);

    const handleAdvancedDraftChange = useCallback((id, field, value) => {
      setAdvancedDraftsById((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      }));
    }, []);

    const handleAdvancedBlur = useCallback(
      (id) => {
        const draft = advancedDraftsById[String(id)];
        if (!draft) {
          return;
        }

        onCardPointAdvancedDataUpdate(String(id), {
          value: Number(draft.value) || 0,
          description: draft.description ?? '',
        });
      },
      [advancedDraftsById, onCardPointAdvancedDataUpdate],
    );

    const handleAdvancedDelete = useCallback(
      (id) => {
        onCardPointAdvancedDataDelete(String(id));
      },
      [onCardPointAdvancedDataDelete],
    );

    useEffect(() => {
      if (!isPointSystemEnabled || !cardPointData) {
        return;
      }

      // Если contentCardDefaults еще не загружены, не инициализируем
      if (!contentCardDefaults || contentCardDefaults.length === 0) {
        return;
      }

      if (isPointSystemInitialized.current) {
        return;
      }

      const ccdId = cardPointData.ccdId ? String(cardPointData.ccdId) : '';
      if (!ccdId) {
        isPointSystemInitialized.current = true;
        return;
      }

      const selected = contentCardDefaults.find((it) => String(it.id) === ccdId);
      // Проверяем, что ContentCardDefault действительно существует в списке доступных
      // (т.е. он принадлежит правильному pointSystemId и не был удален)
      if (!selected) {
        // Если ContentCardDefault не найден, сбрасываем ccdId в CardPointData
        // чтобы избежать ссылки на несуществующий ContentCardDefault
        if (cardPointData.ccdId) {
          onCardPointDataUpsert({ ccdId: null });
        }
        isPointSystemInitialized.current = true;
        return;
      }

      setSelectedCategory(selected.type || '');
      setSelectedCcdId(ccdId);
      isPointSystemInitialized.current = true;
    }, [isPointSystemEnabled, cardPointData, contentCardDefaults, onCardPointDataUpsert]);

    const categoryOptions = useMemo(
      () => [
        { key: 'photo', text: 'Фотография', value: 'photo' },
        { key: 'video', text: 'Видео', value: 'video' },
        { key: 'case', text: 'Кейсы', value: 'case' },
      ],
      [],
    );

    const ccdOptions = useMemo(() => {
      if (!selectedCategory) {
        return [];
      }

      const filtered = contentCardDefaults.filter((it) => {
        // Сравниваем типы, учитывая что type может быть null или undefined
        const itemType = it.type || '';
        const selectedType = selectedCategory || '';
        return itemType === selectedType;
      });

      return filtered.map((it) => {
        const name = it.name || it.description || `Тип #${it.id}`;
        const value = it.value ?? 0;
        return {
          key: String(it.id),
          value: String(it.id),
          text: `${name} (баллов: ${value})`,
        };
      });
    }, [contentCardDefaults, selectedCategory]);

    const isPointSystemLocked = cardPointData?.closed === true;
    const isPointSystemClearDisabled =
      !canEdit ||
      isPointSystemLocked ||
      (!selectedCategory &&
        !selectedCcdId &&
        !cardPointData?.ccdId &&
        (Number(cardPointData?.views) || 0) === 0);

    const handlePointSystemClear = useCallback(() => {
      setSelectedCategory('');
      setSelectedCcdId('');

      onCardPointDataUpsert({ ccdId: null, views: 0 });
    }, [onCardPointDataUpsert]);

    // Вычисляем коэффициент + признак наличия подходящего диапазона DampingCoefficient
    const { coefficient, hasDampingCoefficientMatch, isCoefficientComputable } = useMemo(() => {
      if (!isPointSystemEnabled || !cardPointData) {
        return { coefficient: 1, hasDampingCoefficientMatch: true, isCoefficientComputable: false };
      }

      // Получаем ContentCardDefault
      const ccdId = cardPointData.ccdId ? String(cardPointData.ccdId) : '';
      const contentCardDefault = ccdId && contentCardDefaults
        ? contentCardDefaults.find((it) => String(it.id) === ccdId)
        : null;

      // Без contentViewPlan мы не можем корректно посчитать percent (viewsBase неизвестен),
      // поэтому не показываем ошибку и не блокируем закрытие, пока план не загрузится.
      if (!contentViewPlan) {
        return { coefficient: 1, hasDampingCoefficientMatch: true, isCoefficientComputable: false };
      }

      if (!contentCardDefault || !dampingCoefficients || dampingCoefficients.length === 0) {
        return { coefficient: 1, hasDampingCoefficientMatch: true, isCoefficientComputable: false };
      }

      // Повторяем логику подбора диапазона из calculateCoefficient(),
      // чтобы понимать, найден ли подходящий DampingCoefficient.
      let viewsBase = 0;
      if (contentViewPlan) {
        const type = contentCardDefault.type;
        if (type === 'photo') {
          viewsBase = contentViewPlan.photo ?? 0;
        } else if (type === 'video') {
          viewsBase = contentViewPlan.video ?? 0;
        } else if (type === 'case') {
          viewsBase = contentViewPlan.caseCount ?? 0;
        }
      }

      const viewsReal = cardPointData.views ?? 0;
      const viewRange = viewsReal - viewsBase;

      const INFINITY_VALUE = 1000000000;
      const isInfinity = (value) => value === null || value === INFINITY_VALUE;

      let percent;
      if (viewsBase <= 0) {
        percent = viewsReal === 0 ? 0 : Infinity;
      } else {
        percent = (viewRange / viewsBase) * 100;
      }

      let matchingCoefficient = null;
      for (const dc of dampingCoefficients) {
        const from = dc.from;
        const to = dc.to;

        const matchesFrom = isInfinity(from) || percent >= from;
        const matchesTo = isInfinity(to) || percent <= to;

        if (matchesFrom && matchesTo) {
          matchingCoefficient = dc;
          break;
        }
      }

      if (!matchingCoefficient) {
        return { coefficient: 0, hasDampingCoefficientMatch: false, isCoefficientComputable: true };
      }

      const realCoef = calculateCoefficient({
        contentCardDefault,
        cardPointData,
        contentViewPlan,
        dampingCoefficients,
      });

      return { coefficient: realCoef, hasDampingCoefficientMatch: true, isCoefficientComputable: true };
    }, [isPointSystemEnabled, cardPointData, contentCardDefaults, contentViewPlan, dampingCoefficients]);

    const shouldShowNoCoefficientMessage = isCoefficientComputable && !hasDampingCoefficientMatch;

    useEffect(() => {
      shouldShowNoCoefficientMessageRef.current = shouldShowNoCoefficientMessage;
      if (!shouldShowNoCoefficientMessage) {
        setShowCloseBlockedMessage(false);
      }
    }, [shouldShowNoCoefficientMessage]);

    // Вычисляем общее количество баллов с учетом коэффициента и черновиков
    const totalPoints = useMemo(() => {
      if (!isPointSystemEnabled || !cardPointData) {
        return 0;
      }

      // Получаем ContentCardDefault
      const ccdId = cardPointData.ccdId ? String(cardPointData.ccdId) : '';
      const contentCardDefault = ccdId && contentCardDefaults
        ? contentCardDefaults.find((it) => String(it.id) === ccdId)
        : null;

      // Считаем базовые баллы
      let basePoints = 0;
      if (contentCardDefault && contentCardDefault.value) {
        basePoints = contentCardDefault.value;
      }

      // Считаем дополнительные баллы с учетом черновиков
      let extraPoints = 0;
      (cardPointAdvancedData || []).forEach((it) => {
        const draft = advancedDraftsById[String(it.id)];
        const value = draft ? Number(draft.value) || 0 : (it.value ?? 0);
        extraPoints += value;
      });

      // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
      return (basePoints + extraPoints) * coefficient;
    }, [
      isPointSystemEnabled,
      cardPointData,
      contentCardDefaults,
      cardPointAdvancedData,
      advancedDraftsById,
      coefficient,
    ]);

    const handleCompleteTaskConfirm = useCallback(() => {
      // Проверяем, что только superuser может закрывать задачу
      if (!isSuperuser) {
        return;
      }

      // Проверяем валидность данных перед закрытием задачи
      if (hasInvalidAdvancedData) {
        setShowValidationMessage(true);
        // Скрываем сообщение через 3 секунды
        setTimeout(() => {
          setShowValidationMessage(false);
        }, 3000);
        return;
      }

      // Закрываем конфирм
      closePopup();
      
      // Показываем модальное окно с выбором даты только если включена система баллов
      if (isPointSystemEnabled) {
        setIsTaskCloseDatePopupOpen(true);
      } else {
        // Если система баллов отключена, завершаем задачу без выбора даты
        onCardPointDataComplete();
      }
    }, [isPointSystemEnabled, onCardPointDataComplete, isSuperuser, hasInvalidAdvancedData]);

    const handleTaskCloseDateApply = useCallback(
      (taskClosedAt) => {
        setIsTaskCloseDatePopupOpen(false);
        const listYear = currentListReportData?.year ? Number(currentListReportData.year) : null;
        const listMonth = currentListReportData?.month ? Number(currentListReportData.month) : null;

        if (taskClosedAt && listYear && listMonth) {
          const d = new Date(taskClosedAt);
          const taskYear = d.getFullYear();
          const taskMonth = d.getMonth() + 1;

          if (taskYear !== listYear || taskMonth !== listMonth) {
            setPendingTaskClosedAt(taskClosedAt);
            setIsMonthMismatchModalOpen(true);
            return;
          }
        }

        setPendingTaskClosedAt(null);
        onCardPointDataComplete(taskClosedAt);
      },
      [onCardPointDataComplete, currentListReportData],
    );

    const handleTaskCloseDateClose = useCallback(() => {
      setIsTaskCloseDatePopupOpen(false);
    }, []);

    const handleKeepInCurrentListClick = useCallback(() => {
      setIsMonthMismatchModalOpen(false);
      if (pendingTaskClosedAt) {
        onCardPointDataComplete(pendingTaskClosedAt);
      }
      setPendingTaskClosedAt(null);
    }, [pendingTaskClosedAt, onCardPointDataComplete]);

    const handleMoveToOtherListClick = useCallback(() => {
      setIsMonthMismatchModalOpen(false);
      setSelectedMoveListId('');
      setIsMoveListModalOpen(true);
    }, []);

    const handleMoveListModalClose = useCallback(() => {
      setIsMoveListModalOpen(false);
      setSelectedMoveListId('');
      setPendingTaskClosedAt(null);
    }, []);

    const selectedMoveList = useMemo(
      () => transferLists?.find((l) => String(l.id) === String(selectedMoveListId)) || null,
      [transferLists, selectedMoveListId],
    );

    const pendingClosedAtYearMonth = useMemo(() => {
      if (!pendingTaskClosedAt) {
        return null;
      }

      const d = new Date(pendingTaskClosedAt);
      if (Number.isNaN(d.getTime())) {
        return null;
      }

      return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
      };
    }, [pendingTaskClosedAt]);

    const filteredTransferLists = useMemo(() => {
      if (!transferLists || transferLists.length === 0) {
        return [];
      }

      if (!pendingClosedAtYearMonth?.year || !pendingClosedAtYearMonth?.month) {
        return transferLists;
      }

      return transferLists.filter((l) => {
        const y = l?.reportData?.year ? Number(l.reportData.year) : null;
        const m = l?.reportData?.month ? Number(l.reportData.month) : null;
        return y === pendingClosedAtYearMonth.year && m === pendingClosedAtYearMonth.month;
      });
    }, [transferLists, pendingClosedAtYearMonth]);

    const shouldConfirmMoveToSelectedList = useMemo(() => {
      if (!pendingTaskClosedAt || !selectedMoveList?.reportData?.year || !selectedMoveList?.reportData?.month) {
        return false;
      }

      const listYear = Number(selectedMoveList.reportData.year);
      const listMonth = Number(selectedMoveList.reportData.month);
      if (!listYear || !listMonth) {
        return false;
      }

      const d = new Date(pendingTaskClosedAt);
      const taskYear = d.getFullYear();
      const taskMonth = d.getMonth() + 1;

      return taskYear !== listYear || taskMonth !== listMonth;
    }, [pendingTaskClosedAt, selectedMoveList]);

    const handleMoveToSelectedListConfirm = useCallback(() => {
      if (!pendingTaskClosedAt || !selectedMoveListId) {
        return;
      }

      onMove(String(selectedMoveListId), 0);
      onCardPointDataComplete(pendingTaskClosedAt);
      setIsMoveListModalOpen(false);
      setSelectedMoveListId('');
      setPendingTaskClosedAt(null);
    }, [pendingTaskClosedAt, selectedMoveListId, onMove, onCardPointDataComplete]);

    const handleUncompleteTaskConfirm = useCallback(() => {
      onCardPointDataUncomplete();
      closePopup();
    }, [onCardPointDataUncomplete]);

    // Закрываем попап подтверждения после успешного закрытия задачи
    useEffect(() => {
      const wasClosed = wasTaskClosedRef.current;
      const isClosed = cardPointData?.closed === true;
      
      if (!wasClosed && isClosed) {
        // Задача была только что закрыта, закрываем попап
        // Используем небольшой таймаут, чтобы дать время обновиться состоянию
        const timeoutId = setTimeout(() => {
          // Находим и закрываем попап через клик на кнопку закрытия
          const popupElement = document.querySelector('[class*="Popup"][class*="wrapper"] [class*="closeButton"]');
          if (popupElement) {
            popupElement.click();
          }
        }, 200);
        
        return () => clearTimeout(timeoutId);
      }
      
      wasTaskClosedRef.current = isClosed;
    }, [cardPointData?.closed]);

    const contentNode = (
      <Grid className={styles.grid}>
        <Grid.Row className={styles.headerPadding}>
          <Grid.Column width={16} className={styles.headerPadding}>
            <div className={styles.headerWrapper}>
              <Icon name="list alternate outline" className={styles.moduleIcon} />
              <div className={styles.headerTitleWrapper}>
                {canEdit ? (
                  <NameField defaultValue={name} onUpdate={handleNameUpdate} />
                ) : (
                  <div className={styles.headerTitle}>{name}</div>
                )}
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className={styles.modalPadding}>
          <Grid.Column width={canEdit ? 12 : 16} className={styles.contentPadding}>
            {(users.length > 0 ||
              subscribers.length > 0 ||
              labels.length > 0 ||
              dueDate ||
              (reminderEnabled && reminderDate) ||
              stopwatch) && (
              <div className={styles.moduleWrapper}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {users.length > 0 && (
                    <div className={styles.attachments}>
                      <div className={styles.text}>
                        {t('common.members', {
                          context: 'title',
                        })}
                      </div>
                      {users.map((user) => (
                        <span key={user.id} className={styles.attachment}>
                          {canEdit ? (
                            <BoardMembershipsPopup
                              items={allBoardMemberships}
                              currentUserIds={userIds}
                              onUserSelect={handleUserSelect}
                              onUserDeselect={onUserRemove}
                            >
                              <User name={user.name} avatarUrl={user.avatarUrl} />
                            </BoardMembershipsPopup>
                          ) : (
                            <User name={user.name} avatarUrl={user.avatarUrl} />
                          )}
                        </span>
                      ))}
                      {canEdit && (
                        <BoardMembershipsPopup
                          items={allBoardMemberships}
                          currentUserIds={userIds}
                          onUserSelect={handleUserSelect}
                          onUserDeselect={onUserRemove}
                        >
                          <button
                            type="button"
                            className={classNames(styles.attachment, styles.dueDate)}
                          >
                            <Icon name="add" size="small" className={styles.addAttachment} />
                          </button>
                        </BoardMembershipsPopup>
                      )}
                    </div>
                  )}
                  {subscribers.length > 0 && (
                    <div className={styles.attachments}>
                      <div className={styles.text}>
                        {t('common.subscribers', {
                          context: 'title',
                        })}
                      </div>
                      {subscribers.map((user) => (
                        <span key={user.id} className={styles.attachment}>
                          <button
                            type="button"
                            onClick={handleToggleSubscriptionClick}
                            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                          >
                            <User name={user.name} avatarUrl={user.avatarUrl} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {labels.length > 0 && (
                  <div className={styles.attachments}>
                    <div className={styles.text}>
                      {t('common.labels', {
                        context: 'title',
                      })}
                    </div>
                    {labels.map((label) => (
                      <span key={label.id} className={styles.attachment}>
                        {canEdit ? (
                          <LabelsPopup
                            key={label.id}
                            items={allLabels}
                            currentIds={labelIds}
                            onSelect={onLabelAdd}
                            onDeselect={onLabelRemove}
                            onCreate={onLabelCreate}
                            onUpdate={onLabelUpdate}
                            onMove={onLabelMove}
                            onDelete={onLabelDelete}
                          >
                            <Label name={label.name} color={label.color} />
                          </LabelsPopup>
                        ) : (
                          <Label name={label.name} color={label.color} />
                        )}
                      </span>
                    ))}
                    {canEdit && (
                      <LabelsPopup
                        items={allLabels}
                        currentIds={labelIds}
                        onSelect={onLabelAdd}
                        onDeselect={onLabelRemove}
                        onCreate={onLabelCreate}
                        onUpdate={onLabelUpdate}
                        onMove={onLabelMove}
                        onDelete={onLabelDelete}
                      >
                        <button
                          type="button"
                          className={classNames(styles.attachment, styles.dueDate)}
                        >
                          <Icon name="add" size="small" className={styles.addAttachment} />
                        </button>
                      </LabelsPopup>
                    )}
                  </div>
                )}
                {dueDate && (
                  <div className={styles.attachments}>
                    <div className={styles.text}>
                      {t('common.dueDate', {
                        context: 'title',
                      })}
                    </div>
                    <span className={styles.attachment}>
                      {canEdit ? (
                        <DueDateEditPopup defaultValue={dueDate} onUpdate={handleDueDateUpdate}>
                          <DueDate value={dueDate} />
                        </DueDateEditPopup>
                      ) : (
                        <DueDate value={dueDate} />
                      )}
                    </span>
                  </div>
                )}
                {reminderEnabled && reminderDate && (
                  <div className={styles.attachments}>
                    <div className={styles.text}>Напоминание</div>
                    <span className={styles.attachment}>
                      <DueDate
                        value={reminderDate}
                        isNeutral
                        onClick={canEdit ? handleReminderClick : undefined}
                        isDisabled={!canEdit}
                      />
                    </span>
                  </div>
                )}
                {stopwatch && (
                  <div className={styles.attachments}>
                    <div className={styles.text}>
                      {t('common.stopwatch', {
                        context: 'title',
                      })}
                    </div>
                    <span className={styles.attachment}>
                      {canEdit ? (
                        <StopwatchEditPopup
                          defaultValue={stopwatch}
                          onUpdate={handleStopwatchUpdate}
                        >
                          <Stopwatch startedAt={stopwatch.startedAt} total={stopwatch.total} />
                        </StopwatchEditPopup>
                      ) : (
                        <Stopwatch startedAt={stopwatch.startedAt} total={stopwatch.total} />
                      )}
                    </span>
                    {canEdit && (
                      <button
                        onClick={handleToggleStopwatchClick}
                        type="button"
                        className={classNames(styles.attachment, styles.dueDate)}
                      >
                        <Icon
                          name={stopwatch.startedAt ? 'pause' : 'play'}
                          size="small"
                          className={styles.addAttachment}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {(description || canEdit) && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Icon name="align justify" className={styles.moduleIcon} />
                  <div className={styles.moduleHeader}>{t('common.description')}</div>
                  {canEdit ? (
                    <DescriptionEdit defaultValue={description} onUpdate={handleDescriptionUpdate}>
                      {description ? (
                        <button
                          type="button"
                          className={classNames(styles.descriptionText, styles.cursorPointer)}
                        >
                          <Markdown linkStopPropagation linkTarget="_blank">
                            {description}
                          </Markdown>
                        </button>
                      ) : (
                        <button type="button" className={styles.descriptionButton}>
                          <span className={styles.descriptionButtonText}>
                            {t('action.addMoreDetailedDescription')}
                          </span>
                        </button>
                      )}
                    </DescriptionEdit>
                  ) : (
                    <div className={styles.descriptionText}>
                      <Markdown linkStopPropagation linkTarget="_blank">
                        {description}
                      </Markdown>
                    </div>
                  )}
                </div>
              </div>
            )}
            {(tasks.length > 0 || canEdit) && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Icon name="check square outline" className={styles.moduleIcon} />
                  <div className={styles.moduleHeader}>{t('common.tasks')}</div>
                  <Tasks
                    items={tasks}
                    canEdit={canEdit}
                    onCreate={onTaskCreate}
                    onUpdate={onTaskUpdate}
                    onMove={onTaskMove}
                    onDelete={onTaskDelete}
                  />
                </div>
              </div>
            )}
            {attachments.length > 0 && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Icon name="attach" className={styles.moduleIcon} />
                  <div className={styles.moduleHeader}>{t('common.attachments')}</div>
                  <Attachments
                    items={attachments}
                    canEdit={canEdit}
                    onUpdate={onAttachmentUpdate}
                    onDelete={onAttachmentDelete}
                    onCoverUpdate={handleCoverUpdate}
                    onGalleryOpen={handleGalleryOpen}
                    onGalleryClose={handleGalleryClose}
                  />
                </div>
              </div>
            )}
            {isPointSystemEnabled && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Icon name="star outline" className={styles.moduleIcon} />
                  <div className={styles.moduleHeader}>{t('Балльная система:')}</div>
                  <div className={styles.pointSystemSection}>
                    <Form>
                      <div className={styles.pointSystemFields}>
                        <Form.Select
                          className={styles.categorySelect}
                          placeholder="Категория"
                          options={categoryOptions}
                          value={selectedCategory}
                          disabled={!canEdit || isPointSystemLocked}
                          onChange={(_, { value }) => {
                            const nextCategory = String(value || '');
                            setSelectedCategory(nextCategory);
                            setSelectedCcdId('');
                          }}
                        />
                        <div className={styles.workTypeRow}>
                          <Form.Select
                            className={styles.workTypeSelect}
                            placeholder="Выбрать тип работы"
                            options={ccdOptions}
                            value={selectedCcdId}
                            disabled={!canEdit || isPointSystemLocked || !selectedCategory}
                            onChange={(_, { value }) => {
                              const nextCcdId = String(value || '');
                              setSelectedCcdId(nextCcdId);

                              if (!nextCcdId) {
                                return;
                              }

                              if (cardPointData?.ccdId && String(cardPointData.ccdId) === nextCcdId) {
                                return;
                              }

                              onCardPointDataUpsert({ ccdId: nextCcdId });
                            }}
                          />
                          <Button
                            type="button"
                            icon
                            basic
                            color="red"
                            size="mini"
                            className={styles.clearWorkTypeButton}
                            disabled={isPointSystemClearDisabled}
                            onClick={handlePointSystemClear}
                            title="Удалить"
                            aria-label="Удалить"
                          >
                            <Icon name="trash" />
                          </Button>
                        </div>
                      </div>
                    </Form>

                    <div className={styles.advancedPointsSection}>
                      {(cardPointAdvancedData || []).length ? (
                        <div className={styles.advancedPointsList}>
                          {(cardPointAdvancedData || []).map((it) => (
                            <Form key={it.id} className={styles.advancedPointsRow}>
                              <Form.Field inline className={styles.advancedPointsValue}>
                                <label>Баллы:</label>
                                <input
                                  type="number"
                                  step="1"
                                  value={advancedDraftsById[it.id]?.value ?? 0}
                                  disabled={!canEdit || isPointSystemLocked}
                                  onChange={(e) => handleAdvancedDraftChange(it.id, 'value', e.target.value)}
                                  onBlur={() => handleAdvancedBlur(it.id)}
                                />
                              </Form.Field>
                              <Form.Field inline className={styles.advancedPointsDescription}>
                                <input
                                  value={advancedDraftsById[it.id]?.description ?? ''}
                                  placeholder="Комментарий с причиной начисления"
                                  disabled={!canEdit || isPointSystemLocked}
                                  onChange={(e) =>
                                    handleAdvancedDraftChange(it.id, 'description', e.target.value)
                                  }
                                  onBlur={() => handleAdvancedBlur(it.id)}
                                  style={{
                                    border: (() => {
                                      const draft = advancedDraftsById[String(it.id)];
                                      const value = draft ? Number(draft.value) || 0 : (it.value ?? 0);
                                      const description = draft ? (draft.description ?? '') : (it.description ?? '');
                                      
                                      if (value !== 0 && (!description || description.trim() === '')) {
                                        return '1px solid red';
                                      }
                                      return undefined;
                                    })(),
                                  }}
                                />
                              </Form.Field>
                              <DeletePopup
                                title="Удаление"
                                content="Удалить запись?"
                                buttonContent="Удалить"
                                onConfirm={() => handleAdvancedDelete(it.id)}
                              >
                                <Button
                                  type="button"
                                  icon
                                  basic
                                  size="mini"
                                  className={styles.advancedPointsDeleteButton}
                                  disabled={!canEdit || isPointSystemLocked}
                                >
                                  <Icon name="trash" />
                                </Button>
                              </DeletePopup>
                            </Form>
                          ))}
                        </div>
                      ) : null}

                      <div className={styles.advancedPointsActions}>
                        <div className={styles.totalPointsInfo}>
                          <div className={styles.totalPointsLabel}>
                            Итого: {formatPoints(totalPoints)}
                          </div>
                          <div className={styles.coefficientLabel}>
                            Коэффициент:{' '}
                            {Number.isFinite(coefficient) ? (coefficient === 0 ? '0' : coefficient.toFixed(2)) : '0'}
                          </div>
                        </div>
                        <Button
                          type="button"
                          positive
                          content="Добавить специальные баллы"
                          onClick={onCardPointAdvancedDataCreate}
                          disabled={!canEdit || isPointSystemLocked}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isPointSystemEnabled && shouldShowNoCoefficientMessage && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Message negative size="tiny" compact className={styles.viewsErrorMessage}>
                    Для заданного значения просмотров отсутствует коэфициент
                  </Message>
                </div>
              </div>
            )}
            {isPointSystemEnabled && (
              <div className={styles.contentModule}>
                <div className={styles.moduleWrapper}>
                  <Icon name="eye" className={styles.moduleIcon} />
                  <div className={styles.viewsHeaderRow}>
                    <div className={styles.moduleHeader}>
                      {t('Просмотры публикации в социальных сетях')}
                    </div>
                    <Form className={styles.viewsForm}>
                      <Form.Input
                        type="number"
                        step="1"
                        min="0"
                        className={styles.viewsInput}
                        value={cardPointData?.views ?? 0}
                        disabled={!canEdit || isPointSystemLocked || !(cardPointData?.ccdId || selectedCcdId)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const views = Math.max(0, Math.trunc(Number(raw) || 0));
                          const ccdId = cardPointData?.ccdId
                            ? String(cardPointData.ccdId)
                            : selectedCcdId || undefined;
                          if (!ccdId) {
                            return;
                          }
                          onCardPointDataUpsert({ ccdId, views });
                        }}
                      />
                    </Form>
                  </div>
                </div>
              </div>
            )}
            <Activities
              items={activities}
              isFetching={isActivitiesFetching}
              isAllFetched={isAllActivitiesFetched}
              isDetailsVisible={isActivitiesDetailsVisible}
              isDetailsFetching={isActivitiesDetailsFetching}
              canEdit={canEditCommentActivities}
              canEditAllComments={canEditAllCommentActivities}
              onFetch={onActivitiesFetch}
              onDetailsToggle={onActivitiesDetailsToggle}
              onCommentCreate={onCommentActivityCreate}
              onCommentUpdate={onCommentActivityUpdate}
              onCommentDelete={onCommentActivityDelete}
            />
          </Grid.Column>
          {canEdit && (
            <Grid.Column width={4} className={styles.sidebarPadding}>
              <div className={styles.actions}>
                <span className={styles.actionsTitle}>{t('action.addToCard')}</span>
                <BoardMembershipsPopup
                  items={allBoardMemberships}
                  currentUserIds={userIds}
                  onUserSelect={handleUserSelect}
                  onUserDeselect={onUserRemove}
                >
                  <Button fluid className={styles.actionButton}>
                    <Icon name="user outline" className={styles.actionIcon} />
                    {t('common.members')}
                  </Button>
                </BoardMembershipsPopup>
                <LabelsPopup
                  items={allLabels}
                  currentIds={labelIds}
                  onSelect={onLabelAdd}
                  onDeselect={onLabelRemove}
                  onCreate={onLabelCreate}
                  onUpdate={onLabelUpdate}
                  onMove={onLabelMove}
                  onDelete={onLabelDelete}
                >
                  <Button fluid className={styles.actionButton}>
                    <Icon name="bookmark outline" className={styles.actionIcon} />
                    {t('common.labels')}
                  </Button>
                </LabelsPopup>
                <DueDateEditPopup defaultValue={dueDate} onUpdate={handleDueDateUpdate}>
                  <Button fluid className={styles.actionButton}>
                    <Icon name="calendar check outline" className={styles.actionIcon} />
                    {t('common.dueDate', {
                      context: 'title',
                    })}
                  </Button>
                </DueDateEditPopup>
                <Button
                  fluid
                  type="button"
                  className={classNames(styles.actionButton, reminderEnabled && styles.actionButtonActive)}
                  onClick={handleReminderClick}
                >
                  <Icon name="clock outline" className={styles.actionIcon} />
                  {reminderButtonText}
                </Button>
                {false && (
                  <StopwatchEditPopup defaultValue={stopwatch} onUpdate={handleStopwatchUpdate}>
                    <Button fluid className={styles.actionButton}>
                      <Icon name="clock outline" className={styles.actionIcon} />
                      {t('common.stopwatch')}
                    </Button>
                  </StopwatchEditPopup>
                )}
                <AttachmentAddPopup onCreate={onAttachmentCreate}>
                  <Button fluid className={styles.actionButton}>
                    <Icon name="attach" className={styles.actionIcon} />
                    {t('common.attachment')}
                  </Button>
                </AttachmentAddPopup>
              </div>
              <div className={styles.actions}>
                <span className={styles.actionsTitle}>{t('Действия')}</span>
                <Button
                  fluid
                  className={styles.actionButton}
                  onClick={handleToggleSubscriptionClick}
                >
                  <Icon name="paper plane outline" className={styles.actionIcon} />
                  {isSubscribed ? t('action.unsubscribe') : t('action.subscribe')}
                </Button>
                <CardMovePopup
                  projectsToLists={allProjectsToLists}
                  defaultPath={{
                    projectId,
                    boardId,
                    listId,
                  }}
                  onMove={onMove}
                  onTransfer={onTransfer}
                  onBoardFetch={onBoardFetch}
                >
                  <Button
                    fluid
                    className={styles.actionButton}
                    onClick={handleToggleSubscriptionClick}
                  >
                    <Icon name="share square outline" className={styles.actionIcon} />
                    {t('action.move')}
                  </Button>
                </CardMovePopup>
                <DeletePopup
                  title="Архив"
                  content="Поместить карточку в архив?"
                  buttonContent="Архивировать"
                  onConfirm={onArchive}
                >
                  <Button fluid className={styles.actionButton}>
                    <Icon name="trash alternate outline" className={styles.actionIcon} />
                    {t('В архив')}
                  </Button>
                </DeletePopup>
              </div>
              <div className={styles.updatedAtFormated}>
                <div className={styles.updatedAtFormatedLabel}>Дата создания по МСК</div>
                {updatedAtFormated}
              </div>
              {closedAtFormated && (
                <div className={styles.updatedAtFormated}>
                  <div className={styles.updatedAtFormatedLabel}>Дата закрытия по МСК</div>
                  {closedAtFormated}
                </div>
              )}
              <div className={styles.updatedAtFormated}>
                <div className={styles.updatedAtFormatedLabel}>Кто создал задачу:</div>
                {author}
              </div>
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
    );

    const mainNode = canEdit ? (
      <AttachmentAddZone onCreate={onAttachmentCreate}>{contentNode}</AttachmentAddZone>
    ) : (
      contentNode
    );

    const isTaskClosed = cardPointData?.closed === true;
    const isCompleteButtonDisabled = !canEdit || isTaskClosed || !isSuperuser;
    const completeButtonText = isPointSystemEnabled 
      ? 'ЗАВЕРШИТЬ ЗАДАЧУ И НАЧИСЛИТЬ БАЛЛЫ' 
      : 'ЗАВЕРШИТЬ ЗАДАЧУ';
    const completeConfirmContent = isPointSystemEnabled 
      ? 'Завершить задачу и начислить баллы?' 
      : 'Завершить задачу?';

    return (
      <>
        <Modal 
          open 
          closeIcon
          centered={false} 
          onClose={handleClose} 
          className={styles.wrapper}
        >
          {showValidationMessage && (
            <Message negative style={{ margin: 0, borderRadius: 0 }}>
              <Message.Header>Ошибка</Message.Header>
              <p>Есть незаполненные обязательные поля</p>
            </Message>
          )}
          {showCloseBlockedMessage && (
            <Message negative style={{ margin: 0, borderRadius: 0 }}>
              <Message.Header>Ошибка</Message.Header>
              <p>Для заданного значения просмотров отсутствует коэфициент</p>
            </Message>
          )}
          {mainNode}
          <Modal.Actions className={styles.modalFooter}>
            {isTaskClosed ? (
              <DeletePopup
                title="Подтверждение"
                content="Отменить завершение задачи?"
                buttonContent="Да"
                onConfirm={handleUncompleteTaskConfirm}
              >
                <Button
                  type="button"
                  className={styles.completeTaskButton}
                  disabled={!canEdit}
                >
                  Отменить завершение задачи
                </Button>
              </DeletePopup>
            ) : (
              <DeletePopup
                title="Подтверждение"
                content={completeConfirmContent}
                buttonContent="Да"
                onConfirm={handleCompleteTaskConfirm}
              >
                <Button
                  type="button"
                  className={styles.completeTaskButton}
                  disabled={isCompleteButtonDisabled}
                >
                  {completeButtonText}
                </Button>
              </DeletePopup>
            )}
          </Modal.Actions>
        </Modal>
        <Modal
          open={isTaskCloseDatePopupOpen}
          onClose={handleTaskCloseDateClose}
          size="small"
          centered={false}
          closeIcon
          className={styles.taskCloseDateModal}
        >
          <TaskCloseDateStep
            defaultValue={new Date()}
            onApply={handleTaskCloseDateApply}
            onClose={handleTaskCloseDateClose}
          />
        </Modal>
        <Modal
          open={isReminderModalOpen}
          onClose={handleReminderClose}
          size="small"
          centered={false}
          closeIcon
          className={styles.taskDueDateModal}
        >
          <CardReminderStep
            defaultValue={reminderDate || undefined}
            defaultEnabled={reminderEnabled}
            onApply={handleReminderApply}
            onClose={handleReminderClose}
          />
        </Modal>
        <Modal
          open={isMonthMismatchModalOpen}
          onClose={() => {
            setIsMonthMismatchModalOpen(false);
            setPendingTaskClosedAt(null);
          }}
          size="small"
          centered={false}
          closeOnDimmerClick={false}
          closeIcon
        >
          <Modal.Header>Подтверждение</Modal.Header>
          <Modal.Content>
            <div>Выбранная дата не соответствует текущему месяцу в списке</div>
          </Modal.Content>
          <Modal.Actions>
            <Button type="button" onClick={handleKeepInCurrentListClick}>
              Оставить в текущем списке
            </Button>
            <Button type="button" primary onClick={handleMoveToOtherListClick}>
              Перенести задачу в другой список
            </Button>
          </Modal.Actions>
        </Modal>
        <Modal
          open={isMoveListModalOpen}
          onClose={handleMoveListModalClose}
          size="small"
          centered={false}
          closeOnDimmerClick={false}
          closeIcon
        >
          <Modal.Header>Перенос задачи</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Select
                placeholder="Выберите список"
                options={(filteredTransferLists || []).map((l) => ({
                  key: l.id,
                  text: l.name,
                  value: l.id,
                }))}
                value={selectedMoveListId}
                onChange={(_, { value }) => setSelectedMoveListId(String(value || ''))}
              />
              {pendingClosedAtYearMonth && filteredTransferLists.length === 0 && (
                <div style={{ marginTop: '10px', opacity: 0.75 }}>
                  Нет списков за выбранный месяц ({pendingClosedAtYearMonth.month}.{pendingClosedAtYearMonth.year})
                </div>
              )}
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button type="button" onClick={handleMoveListModalClose}>
              Отмена
            </Button>
            {shouldConfirmMoveToSelectedList ? (
              <DeletePopup
                title="Подтверждение"
                content="Выбранный список не соответствует месяцу выбранной даты. Перенести задачу?"
                buttonContent="Да"
                onConfirm={handleMoveToSelectedListConfirm}
              >
                <Button type="button" positive disabled={!selectedMoveListId}>
                  Перенести
                </Button>
              </DeletePopup>
            ) : (
              <Button
                type="button"
                positive
                disabled={!selectedMoveListId}
                onClick={handleMoveToSelectedListConfirm}
              >
                Перенести
              </Button>
            )}
          </Modal.Actions>
        </Modal>
      </>
    );
  },
);

CardModal.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  author: PropTypes.string,
  createdAt:PropTypes.string,
  dueDate: PropTypes.instanceOf(Date),
  reminderDate: PropTypes.instanceOf(Date),
  reminderEnabled: PropTypes.bool,
  stopwatch: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isSubscribed: PropTypes.bool.isRequired,
  isPointSystemEnabled: PropTypes.bool,
  isActivitiesFetching: PropTypes.bool.isRequired,
  isAllActivitiesFetched: PropTypes.bool.isRequired,
  isActivitiesDetailsVisible: PropTypes.bool.isRequired,
  isActivitiesDetailsFetching: PropTypes.bool.isRequired,
  listId: PropTypes.string.isRequired,
  boardId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  cardId: PropTypes.string,
  pointSystemId: PropTypes.string,
  /* eslint-disable react/forbid-prop-types */
  users: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  tasks: PropTypes.array.isRequired,
  attachments: PropTypes.array.isRequired,
  activities: PropTypes.array.isRequired,
  allProjectsToLists: PropTypes.array.isRequired,
  allBoardMemberships: PropTypes.array.isRequired,
  allLabels: PropTypes.array.isRequired,
  contentCardDefaults: PropTypes.array,
  cardPointData: PropTypes.object,
  cardPointAdvancedData: PropTypes.array,
  cardPointArchivedData: PropTypes.object,
  contentViewPlan: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  dampingCoefficients: PropTypes.array,
  /* eslint-enable react/forbid-prop-types */
  canEdit: PropTypes.bool.isRequired,
  canEditCommentActivities: PropTypes.bool.isRequired,
  canEditAllCommentActivities: PropTypes.bool.isRequired,
  currentUser: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isSuperuser: PropTypes.bool.isRequired,
  currentListReportData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  transferLists: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  onContentCardDefaultsFetch: PropTypes.func.isRequired,
  onDampingCoefficientsFetch: PropTypes.func.isRequired,
  onCardPointDataFetch: PropTypes.func.isRequired,
  onCardPointDataUpsert: PropTypes.func.isRequired,
  onCardPointDataComplete: PropTypes.func.isRequired,
  onCardPointDataUncomplete: PropTypes.func.isRequired,
  onCardPointAdvancedDataFetch: PropTypes.func.isRequired,
  onCardPointAdvancedDataCreate: PropTypes.func.isRequired,
  onCardPointAdvancedDataUpdate: PropTypes.func.isRequired,
  onCardPointAdvancedDataDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onUserAdd: PropTypes.func.isRequired,
  onUserRemove: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onLabelAdd: PropTypes.func.isRequired,
  onLabelRemove: PropTypes.func.isRequired,
  onLabelCreate: PropTypes.func.isRequired,
  onLabelUpdate: PropTypes.func.isRequired,
  onLabelMove: PropTypes.func.isRequired,
  onLabelDelete: PropTypes.func.isRequired,
  onTaskCreate: PropTypes.func.isRequired,
  onTaskUpdate: PropTypes.func.isRequired,
  onTaskMove: PropTypes.func.isRequired,
  onTaskDelete: PropTypes.func.isRequired,
  onAttachmentCreate: PropTypes.func.isRequired,
  onAttachmentUpdate: PropTypes.func.isRequired,
  onAttachmentDelete: PropTypes.func.isRequired,
  onActivitiesFetch: PropTypes.func.isRequired,
  onActivitiesDetailsToggle: PropTypes.func.isRequired,
  onCommentActivityCreate: PropTypes.func.isRequired,
  onCommentActivityUpdate: PropTypes.func.isRequired,
  onCommentActivityDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CardModal.defaultProps = {
  description: undefined,
  dueDate: undefined,
  reminderDate: undefined,
  reminderEnabled: false,
  stopwatch: undefined,
  isPointSystemEnabled: false,
  cardId: undefined,
  pointSystemId: undefined,
  contentCardDefaults: [],
  cardPointData: null,
  cardPointAdvancedData: [],
  currentUser: null,
  isSuperuser: false,
  currentListReportData: null,
  transferLists: [],
};

export default CardModal;
